import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFirebaseCRUD } from "../../hooks/useFirebaseCRUD";
import DropdownField from "../Form/DropdownField";
import TextField from "../Form/TextField";
import Button from "../Button";
import ContextMenu from "../ContextMenu";
import Columns from "../Columns";
import Spinner from "../Spinner";

export default function CreateUserGroupBinding({ user }) {
  const navigate = useNavigate();
  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  
  // Use the Firebase CRUD hook
  const { 
    getCollection,
    getDocument,
    createDocument,
    addToArray,
    loading,
    error
  } = useFirebaseCRUD();

  // Fetch groups from Firestore
  useEffect(() => {
    async function fetchGroups() {
      try {
        if (!user?.organization?.id) return;

        // Use reusable hook to get collection
        const groupsArr = await getCollection(
          `organizations/${user.organization.id}/user-groups`
        );

        // Filter out groups already assigned to the user
        const filtered = groupsArr.filter(
          (group) =>
            !user.groupBindings.find(
              (binding) => String(binding.group.id) === String(group.id)
            )
        );

        setUserGroups(filtered);
      } catch (err) {
        console.error("Error fetching groups:", err);
      }
    }

    fetchGroups();
  }, [user]);

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      if (!selectedGroupId) {
        alert("Please select a group");
        return;
      }

      // 1️⃣ Add to user-group-bindings subcollection using reusable hook
      await createDocument(
        `organizations/${user.organization.id}/user-group-bindings/binding_${user.id}_${selectedGroupId}`,
        {
          userId: user.id,
          groupId: selectedGroupId,
          createdAt: new Date(),
        }
      );

      // 2️⃣ Fetch group details using reusable hook
      const groupData = await getDocument(
        `organizations/${user.organization.id}/user-groups/${selectedGroupId}`
      );

      if (groupData) {
        // 3️⃣ Update user's groupBindings array using reusable hook
        await addToArray(`users/${user.id}`, 'groupBindings', {
          group: {
            id: groupData.id,
            name: groupData.name,
            color: groupData.color || "#000000",
            icon: groupData.icon || "FaUsers",
            flags: groupData.flags || 0,
          },
        });
      }

      navigate(`/users/${user.id}`);
    } catch (err) {
      console.error("Error assigning group:", err);
      alert(error?.message || "Failed to assign group. Check console for details.");
    }
  }

  if (!user || loading) {
    return <Spinner />;
  }

  return (
    <div className="box">
      <form onSubmit={handleSubmit}>
        <Columns>
          <TextField
            label="Organization"
            value={user.organization.name}
            readOnly={true}
            required={true}
          />
          <TextField
            label="User"
            value={user.email}
            readOnly={true}
            required={true}
          />
        </Columns>

        <DropdownField
          name="groupId"
          label="User group"
          required={true}
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
        >
          <option value="">Select a group</option>
          {userGroups.length > 0 &&
            userGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          {userGroups.length === 0 && <option>No groups available</option>}
        </DropdownField>

        <div className='flex justify-end'>
          <Button type="submit">Add</Button>
        </div>
      </form>
    </div>
  );
}